// The premium armies in a match (2026-10-25): loads an army's match files (scripts/pack_lods.ts, about 2,500 triangles
// each), puts on its props and its siege rig, and bakes every class (baked.ts) so a match draws it like the
// placeholders, one instanced draw per material. An army with models is listed in ARMY_ART (2026-10-26: Samurai,
// Knights, Greeks, Vikings; armies2 the same day: Mongols, Chinese, Aztecs, Egyptians and the neutral camps' guards).
// One without keeps the placeholder soldiers (units.ts).
import * as THREE from 'three';
import { GLTFLoader, type GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { CLASSES, type ClassId, type FactionId } from '../sim/defs';
import { PLAYERS } from '../sim/sim';
import { ARMY_PROPS, aimProps, applyProps, standClip } from './props';
import { CARTLESS, siegeRoot, SiegeRig, spokedWheel, trim, type SiegeKind } from './siege';
import { BakedArmy, bake, type Baked } from './baked';
import { HORSE_H, riderClips, saddleOf, seatRider, strideOf, type Mount } from './mount';

/** The file that plays each class. The Samurai have no footman model yet: their footmen are the swordsman, a size down. */
const FILE: Record<ClassId, string> = { footman: 'line', line: 'line', spear: 'spear', ranged: 'ranged', cavalry: 'cavalry', siege: 'siege', support: 'support', elite: 'elite', hero: 'elite', ram: 'ram' }; // heroes draw as their look's class (main.ts); the ram is made in code
/** Body height (feet to the top of the skull, no hat, crest or banner) before the match's UNIT_SCALE. 2026-10-26: was
 *  the whole model's height, so a tall banner or crest shrank the soldier under it (the line soldier stood 74% of the
 *  drummer). Elite and heroes stand a size up; heroes add main.ts HERO_SCALE. The cavalry's is its rider's standing
 *  height, the siege's its crew's. */
export const BODY: Record<ClassId, number> = { footman: 1.12, line: 1.2, spear: 1.2, ranged: 1.2, cavalry: 1.2, siege: 1.2, support: 1.2, elite: 1.3, hero: 1.3, ram: 1.2 };
/** Head over body height: STYLE.md's "about 4 heads tall, heads a little large". 0.27 (3.7 heads) is the line
 *  soldier as Meshy made him from the logo's sheet, so the army's most common figure is the anchor and keeps his look. */
export const HEAD_SHARE = 0.27;
/** Hat brim half-width over body height, every hat the same size: the line soldier's (0.173 x his head factor 1.0
 *  over his body 0.718). 2026-10-26: the hat grew with the head, so the crew's (head x1.76) came out 16% wider than
 *  the swordsman's and a crowd read as black discs from the match camera. */
export const HAT_SHARE = 0.241;
/** Each file's face, by eye on front orthographic renders in bind pose, every unit scaled by its Head bone's height
 *  (2026-10-26, the units branch report): chin and eye heights over the feet, as shares of the model's rest height. A
 *  head is twice eyes minus chin (these faces have their eyes mid-head), so the skull top under a hat or helmet is chin
 *  plus head. The drummer's drum is fused to the back of his head and reads as one bald dome at play zoom, so his head
 *  runs to the dome's top (`top`), not to twice his eyes. The cavalry has no bones: its rider's
 *  head turns about `at` (x, z off the model's center, at the chin) out to radius r, and its `body` is the standing
 *  height his shoulders imply (shoulder width is 0.497 of the body on the line soldier). `hat`: the straw hat
 *  (jingasa), measured 2026-10-26 in bind pose: its brim's lowest edge and its apex over the feet, its center `at`
 *  (x, z off the Head joint: a circle fit through the brim's edge, the swordsman's sits 0.019 forward), the brim's
 *  half-width, and the head's half-width under it. scripts/lib/atlas.ts paints its lacquer, ribs and rim from the
 *  same numbers. */
export interface Hat { brim: number; top: number; w: number; head: number; at: [number, number] }
/** Whether a point (r off the hat's axis, y over the feet, shares of the rest height) is the hat: out to just past
 *  the rim, from just under the brim's lowest edge to the cone over it, with room at the rim for a tilted brim (the
 *  swordsman's runs 0.04 higher at the back). A banner pole above the apex or a bow beyond the rim stays out. */
export function inHat(s: Hat, r: number, y: number) {
  return r <= 1.08 * s.w && y >= s.brim - 0.02 && y <= s.top - (s.top - s.brim) * (r / s.w) + 0.01 + 0.04 * Math.min(1, r / s.w);
}
export interface Face { chin: number; eyes: number; top?: number; hat?: Hat }
/** The Samurai's faces (ARMY_ART has every army's). */
export const FACE: Record<string, Face> = {
  line: { chin: 0.524, eyes: 0.621, hat: { brim: 0.624, top: 0.755, w: 0.173, head: 0.081, at: [-0.002, 0.019] } },
  spear: { chin: 0.492, eyes: 0.583, hat: { brim: 0.605, top: 0.72, w: 0.173, head: 0.089, at: [0.002, 0.012] } },
  ranged: { chin: 0.530, eyes: 0.628, hat: { brim: 0.631, top: 0.773, w: 0.186, head: 0.092, at: [0, -0.004] } },
  support: { chin: 0.673, eyes: 0.794, top: 0.995 }, elite: { chin: 0.613, eyes: 0.691 },
  cavalry: { chin: 0.655, eyes: 0.725 }, // the rider alone (horses round); the kabuto's horns and crest are a hat
  crew: { chin: 0.761, eyes: 0.840, hat: { brim: 0.864, top: 1.0, w: 0.165, head: 0.086, at: [0.001, 0.005] } },
};

/** Every army with 3D models (2026-10-26): its faces (FACE above, by file), the file its siege crew are made from, and
 *  its engine (siege.ts). The Samurai's crew are their own file; the other armies' are their spearmen, drawn with an
 *  empty fist and rigged with the charge, so the ram's carriers already hold their grip (ops concept r29). Files live
 *  in units/<army>/ (assets/units/<army>/lod for a match). Faces measured on front orthographic bind-pose renders
 *  (scripts/faces.ts), the same way as the Samurai's. */
/** A set of figures: one per faction with models, and the neutral camps' guards (2026-10-26, armies2: they drew as
 *  grey placeholders). */
export type ArmyId = FactionId | 'neutral';
/** A chariot's wheels (bakeChariot): a spoked wheel at each center (x and z off the box center, y up from its foot)
 *  and radius, shares of the car's height. */
export interface Wheels { at: [number, number, number][]; r: number }
/** `crew` and `siege` for an army that fields an engine; `ram` for one that buys a ram as its Extra (its rig and the
 *  file its carriers are, 2026-10-27); `only`: the files a set has, every other class drawing as its line soldier (the
 *  neutral camps field no riders, engines or drummers). */
/** `mounts`: the classes that ride (2026-10-27, horses round), by their file (the rider), each with its horse file
 *  (mount.ts). */
export interface ArmyArt { face: Record<string, Face>; crew?: string; siege?: SiegeKind; ram?: { kind: SiegeKind; crew: string }; only?: string[]; mounts?: Record<string, Mount> }
export const ARMY_ART: Partial<Record<ArmyId, ArmyArt>> = {
  samurai: { face: FACE, crew: 'crew', siege: 'bow', mounts: { cavalry: { horse: 'horse_samurai' } } },
  // top is the helmet's or cap's crown (no crest, plume or bow above it): a helmet is sized with the head it holds
  knights: {
    crew: 'spear', siege: 'trebuchet',
    // the lancer rides a big horse (the old fused lancer's stood half again the sergeant's)
    mounts: { cavalry: { horse: 'horse_grey' }, elite: { horse: 'horse_grey', size: 1.3 } },
    face: {
      // line, spear and ranged re-made on the chunky build (armies2, 2026-10-26)
      line: { chin: 0.734, eyes: 0.834, top: 0.99 }, spear: { chin: 0.77, eyes: 0.865, top: 0.99 },
      ranged: { chin: 0.769, eyes: 0.857, top: 0.99 }, support: { chin: 0.565, eyes: 0.70, top: 0.865 },
      // the riders alone (horses round, 2026-10-27): the lancer's plume is not his head
      cavalry: { chin: 0.67, eyes: 0.80, top: 0.99 }, elite: { chin: 0.70, eyes: 0.79, top: 0.95 },
    },
  },
  greeks: {
    crew: 'spear', siege: 'stone', mounts: { cavalry: { horse: 'horse_chestnut' } },
    face: {
      // spear, ranged and support re-made on the chunky build (armies2, 2026-10-26)
      // the line's chin is his jaw under the beard and cheek guards, not the collar (anatomy canon, 2026-11-01); read
      // as 0.575, 0.685 and 0.855, then x 0.934185 when his crest grew the file from 1.199752 tall to 1.284275 (model
      // fixes, scripts/fix_model.ts): the same jaw, eyes and crown, the crest not his head
      line: { chin: 0.537, eyes: 0.640, top: 0.799 }, spear: { chin: 0.645, eyes: 0.727, top: 0.84 },
      ranged: { chin: 0.658, eyes: 0.801, top: 0.99 }, support: { chin: 0.638, eyes: 0.816 },
      elite: { chin: 0.665, eyes: 0.76, top: 0.865 },
      cavalry: { chin: 0.645, eyes: 0.80, top: 0.93 }, // the rider alone (horses round); top is his skull under the sun hat
    },
  },
  vikings: {
    // the Mangonel is the Greek stone thrower's cart (units/vikings/siege.glb is a copy) with Viking crew; the
    // Battering Ram is their Extra (2026-10-27)
    crew: 'spear', siege: 'stone', ram: { kind: 'ram', crew: 'spear' }, mounts: { cavalry: { horse: 'horse_pony' } },
    face: {
      // the line's chin is his jaw under the beard, not the beard's tip (anatomy canon, 2026-11-01)
      line: { chin: 0.75, eyes: 0.85, top: 0.995 }, spear: { chin: 0.705, eyes: 0.855, top: 0.995 },
      ranged: { chin: 0.638, eyes: 0.802, top: 0.99 }, support: { chin: 0.72, eyes: 0.85, top: 0.995 }, // ranged re-made (armies2)
      elite: { chin: 0.67, eyes: 0.80 }, // the bear's head is a hat
      cavalry: { chin: 0.64, eyes: 0.78, top: 0.995 }, // the rider alone (horses round); his chin under the beard
    },
  },
  // armies2 (2026-10-26): the four last armies and the neutral camps' guards, every file on the chunky toy build
  // (scripts/lib/bulk.ts). A pointed felt hat, a feather crest or a hood over the skull is a hat, out of the head (no
  // top). The mounted riders' head centers, radii and bodies come from out/rider.ts's measure of the file (the head
  // band's median, its 90th percentile radius) checked on the side renders.
  mongols: {
    // the horse archer shoots from a standing pony; the guard lancer rides the bigger chestnut
    crew: 'spear', siege: 'catapult',
    mounts: { cavalry: { horse: 'horse_pony', attack: 'stand' }, elite: { horse: 'horse_chestnut', size: 1.2 } },
    face: {
      line: { chin: 0.614, eyes: 0.76 }, spear: { chin: 0.62, eyes: 0.732 }, ranged: { chin: 0.617, eyes: 0.738 },
      support: { chin: 0.635, eyes: 0.769 },
      // the riders alone (horses round): the fur hat and the pointed helmet are hats
      cavalry: { chin: 0.61, eyes: 0.71 }, elite: { chin: 0.70, eyes: 0.78 },
    },
  },
  chinese: {
    crew: 'spear', siege: 'rockets', mounts: { cavalry: { horse: 'horse_grey' } },
    face: {
      line: { chin: 0.643, eyes: 0.79, top: 0.99 }, spear: { chin: 0.645, eyes: 0.818, top: 0.99 },
      ranged: { chin: 0.703, eyes: 0.84, top: 0.99 }, support: { chin: 0.645, eyes: 0.793, top: 0.985 },
      elite: { chin: 0.793, eyes: 0.884, top: 0.975 },
      cavalry: { chin: 0.71, eyes: 0.80, top: 0.92 }, // the rider alone (horses round); the spike is not his head
    },
  },
  aztecs: {
    // the slinger band is three slingers, the band's own file; the eagle runner is the cavalry class, on foot
    crew: 'slinger', siege: 'slingers',
    face: {
      // the line's chin is his jaw, not his lower lip, and his skull top is under the headband (anatomy canon, 2026-11-01)
      line: { chin: 0.635, eyes: 0.79, top: 0.92 }, spear: { chin: 0.596, eyes: 0.709 }, ranged: { chin: 0.613, eyes: 0.717 },
      support: { chin: 0.755, eyes: 0.836 }, elite: { chin: 0.772, eyes: 0.873 }, // the jaguar's head is a hat
      slinger: { chin: 0.717, eyes: 0.80 },
      cavalry: { chin: 0.717, eyes: 0.815, top: 0.99 }, // the eagle runner: the eagle head is a helmet
    },
  },
  egyptians: {
    // the Archer Band is the slinger band's rig with three Medjay bowmen; the Siege Shed is their Extra (2026-10-27)
    crew: 'ranged', siege: 'slingers', ram: { kind: 'shed', crew: 'spear' },
    // the chariot: the car cut from the old fused model, two rigged horses, the line soldier driving, the bowman shooting
    mounts: { cavalry: { horse: 'horse_chariot', size: 0.95, crew: ['line', 'ranged'] } },
    face: {
      line: { chin: 0.655, eyes: 0.822, top: 0.99 }, spear: { chin: 0.741, eyes: 0.863 },
      ranged: { chin: 0.593, eyes: 0.705, top: 0.84 }, support: { chin: 0.676, eyes: 0.827, top: 0.97 },
      elite: { chin: 0.652, eyes: 0.773, top: 0.945 },
    },
  },
  neutral: {
    only: ['line', 'spear', 'ranged'],
    face: { line: { chin: 0.66, eyes: 0.806, top: 0.99 }, spear: { chin: 0.652, eyes: 0.796, top: 0.99 }, ranged: { chin: 0.67, eyes: 0.806, top: 0.99 } },
  },
};

/** The army whose figures draw heroes (as their look's class) and the giant, until they have their own models. */
export const HERO_ARMY: FactionId = 'samurai';
/** Which army's 3D set draws a soldier: heroes and the giant HERO_ARMY's, everyone else his player's army if it has
 *  models, neutral before the army pick, else none (placeholders). */
export function armyFor(hero: boolean, army: ArmyId | null | undefined): ArmyId | null {
  if (hero) return HERO_ARMY;
  if (army == null) return 'neutral';
  return ARMY_ART[army] ? army : null;
}

/** The file that draws class c in this set. */
export function fileOf(art: ArmyArt, c: ClassId) {
  const f = c === 'ram' && !art.ram ? FILE.siege : FILE[c]; // an army with no ram never fields one
  return art.only && !art.only.includes(f) ? 'line' : f;
}

/** The match's move names, and the clip that plays each (the drummer cheers where the others attack). */
const CLIP_FOR: Record<string, string[]> = { walk: ['walk'], run: ['run'], attack: ['attack', 'cheer'], idle: ['idle'] };

/** `hero`: the same unit drawn for heroes, a few at most, who cast the sun's shadow (look.ts); only the hero army's
 *  set has them (armyFor draws every hero there). */
export interface PremiumClass { army: BakedArmy; hero?: BakedArmy; scale: number; baked: Baked }

/** The files an army's match set downloads (units/<army>/lod): one per class file and the crew's, no cart for an
 *  engine made in code (the ram, the shed, the slingers). */
export function armyFiles(army: ArmyId) {
  const art = ARMY_ART[army]!;
  const mounts = Object.values(art.mounts ?? {}).flatMap((m) => [m.horse, ...m.crew ?? []]);
  return [...new Set([...CLASSES.map((c) => fileOf(art, c)), ...art.crew ? [art.crew] : [], ...art.ram ? [art.ram.crew] : [], ...mounts])]
    .filter((f) => f !== 'ram' && !(f === 'siege' && CARTLESS.includes(art.siege!)));
}

/** When to (re)load an army's set: once, and after a failed download again every `wait` ms, `max` tries in all, so
 *  a dropped connection does not leave an army as placeholders (Astra), nor retry every frame; switching graphics
 *  starts the count again (forgetFailed). */
export class Retry {
  private tries = new Map<string, { n: number; failedAt?: number }>();
  constructor(readonly max = 3, readonly wait = 20_000) {}
  /** True when `key` should start a load now (and counts the try). */
  start(key: string, now: number) {
    const t = this.tries.get(key);
    if (t && (t.failedAt === undefined || t.n >= this.max || now - t.failedAt < this.wait)) return false;
    this.tries.set(key, { n: (t?.n ?? 0) + 1 });
    return true;
  }
  failed(key: string, now: number) { const t = this.tries.get(key); if (t) t.failedAt = now; }
  /** A player's own retry (graphics switched): every failed load may start again, its count from zero. */
  forgetFailed() { for (const [k, t] of this.tries) if (t.failedAt !== undefined) this.tries.delete(k); }
}

/** Loads and bakes one army. `dir` holds its files (units/<army>, or lod beside a tool). */
export async function loadArmy(dir: string, cap: number, army: ArmyId = 'samurai'): Promise<Record<ClassId, PremiumClass>> {
  const art = ARMY_ART[army];
  if (!art) throw new Error(`${army} has no 3D models`);
  const loader = new GLTFLoader().setMeshoptDecoder(MeshoptDecoder);
  // the ram, the shed and the slingers are made in code (siege.ts siegeRoot), no cart to load; the crew get their own
  // parse of their file, since the spearman's own bake puts a pole in his fist and edits his clips
  const all = armyFiles(army), own = new Set(CLASSES.map((c) => fileOf(art, c))), files = all.filter((f) => own.has(f));
  // horses (and a chariot's crew) are parsed where they are used, one parse each
  const bufs = new Map(await Promise.all(all.map(async (f) => {
    const r = await fetch(`${dir}/${f}.glb`);
    if (!r.ok) throw new Error(`${dir}/${f}.glb: HTTP ${r.status}`);
    return [f, await r.arrayBuffer()] as const;
  })));
  const load = (f: string) => loader.parseAsync(bufs.get(f)!, `${dir}/`); // one download, a parse each
  // the ram's carriers are a parse of their own too: a bake sizes its crew's heads in place
  const [crew, ramCrew, ...loaded] = await Promise.all([art.crew ? load(art.crew) : null, art.ram ? load(art.ram.crew) : null, ...files.map(load)]);
  const got = Object.fromEntries(files.map((f, k) => [f, loaded[k]])) as Record<string, GLTF>;
  const bakedBy: Record<string, Baked> = {};
  for (const f of own) {
    const m = art.mounts?.[f];
    // a mounted file gets its own parse of its horse (and of a chariot's crew): the rider is bolted to that horse's bones
    bakedBy[f] = f === 'ram' ? bakeSiege(null, ramCrew!, { ...art, siege: art.ram!.kind, crew: art.ram!.crew })
      : f === 'siege' ? bakeSiege(got.siege ?? null, crew!, art)
      : m?.crew ? bakeChariot(f, got[f], await Promise.all([load(m.horse), load(m.horse)]), await Promise.all(m.crew.map(load)), m, army)
      : m ? bakeMounted(f, got[f], await load(m.horse), m, army)
      : bakeUnit(f, got[f], army);
  }
  return armySet(bakedBy, army, cap);
}

/** An army's classes from its baked files: soldiers for every army, hero batches only in the hero army's set. */
export function armySet(bakedBy: Record<string, Baked>, army: ArmyId, cap: number) {
  const art = ARMY_ART[army] ?? { face: FACE };
  return Object.fromEntries(CLASSES.map((c) => {
    const baked = bakedBy[fileOf(art, c)];
    const scale = BODY[c] / baked.height;
    return [c, { army: new BakedArmy(baked, cap), hero: army === HERO_ARMY ? new BakedArmy(baked, PLAYERS, true) : undefined, scale, baked }];
  })) as Record<ClassId, PremiumClass>;
}

/** The model on a holder, feet at y 0 and centered, so the bake's frame is the soldier's own. */
function hold(scene: THREE.Object3D) {
  const holder = new THREE.Group();
  holder.add(scene);
  const box = new THREE.Box3().setFromObject(scene), c = box.getCenter(new THREE.Vector3());
  scene.position.set(-c.x, -box.min.y, -c.z);
  return holder;
}

/** Grows or shrinks the head to HEAD_SHARE of the body: every clip's Head scale (Meshy keys every bone's scale in
 *  every clip, so the rest pose and the model's box stay as they were), or for the boneless rider his head's
 *  vertices, about the chin. Returns the body's height, feet to skull top, in the model's own units. Call once, right
 *  after load, before props take their grips. A model with no FACE entry is all body (the cart). */
export function proportion(name: string, scene: THREE.Object3D, clips: THREE.AnimationClip[], faces: Record<string, Face> = FACE) {
  scene.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(scene), H = box.max.y - box.min.y, f = faces[name];
  if (!f) return H;
  const h = f.top ? (f.top - f.chin) * H : 2 * (f.eyes - f.chin) * H, chin = f.chin * H, head = scene.getObjectByName('Head');
  if (!head) throw new Error(`${name}: no Head bone to size its head by`);
  // the head bone sits about the mouth: the skull rises a above it, the chin hangs below
  const pivot = head.getWorldPosition(new THREE.Vector3()).y - box.min.y, a = chin + h - pivot;
  const k = (HEAD_SHARE * pivot) / (h - HEAD_SHARE * a);
  for (const clip of clips) {
    const t = clip.tracks.find((x) => x.name === `${head.name}.scale`);
    if (t) t.values = t.values.map((v) => v * k);
    else clip.tracks.push(new THREE.VectorKeyframeTrack(`${head.name}.scale`, [0], head.scale.clone().multiplyScalar(k).toArray()));
  }
  const body = pivot + k * a;
  // the brim's overhang shrinks or grows so that, after the head's k, every hat is HAT_SHARE of its body. Before the
  // rest scale below: trimBrim reads the rest pose, and a scaled head would move every point it measures.
  if (f.hat) trimBrim(scene, head, box.min.y, H, f.hat, ((HAT_SHARE * body) / k / H - f.hat.head) / (f.hat.w - f.hat.head));
  head.scale.multiplyScalar(k); // the rest pose too, for anything drawn without a clip (Flash)
  return body;
}

/** A float copy of a vec3 attribute: the files' are quantized int16 (meshopt), and a vertex moved past the old box
 *  would wrap. */
function floats(q: THREE.BufferAttribute | THREE.InterleavedBufferAttribute) {
  const f = new THREE.Float32BufferAttribute(q.count * 3, 3);
  for (let i = 0; i < q.count; i++) f.setXYZ(i, q.getX(i), q.getY(i), q.getZ(i));
  return f;
}

/** Scales the hat brim's overhang (past the head's half-width, out to the rim) by c about the hat's axis, across
 *  only: the hat keeps its height and still sits on the head. A hat vertex is the head's (Head or below it in the
 *  skeleton) and inHat; normals turn with the surface. The move depends only on where a vertex is and what moves it,
 *  so copies of a point along a seam move as one. 2026-10-26 (Astra, twice): a sliver from the skull's edge (which
 *  stays) to the rim at another angle can flip in place, under 0.1% of the moved area on every file; on these files
 *  other front-facing hat lies behind each one (Astra's probe), so no hole shows. A new model needs that look again.
 *  Halving the moves that flipped opened seams instead, and pulling the whole hat in (no hinge)
 *  squeezed the crew's forehead and flipped as much, so neither shipped. */
function trimBrim(scene: THREE.Object3D, head: THREE.Object3D, minY: number, H: number, hat: Hat, c: number) {
  const axis = head.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(hat.at[0] * H, 0, hat.at[1] * H)), R0 = hat.head * H;
  const v = new THREE.Vector3(), n = new THREE.Vector3();
  scene.traverse((o) => {
    const mesh = o as THREE.SkinnedMesh;
    if (!mesh.isSkinnedMesh) return;
    const sk = mesh.skeleton, hi = sk.bones.indexOf(head as THREE.Bone);
    if (hi < 0) return;
    const under = sk.bones.map((b) => { for (let x: THREE.Object3D | null = b; x; x = x.parent) if (x === head) return true; return false; });
    // a stored position to the world at rest: every bone's skinning matrix is the same there, so the head's stands in
    const A = mesh.matrixWorld.clone().multiply(mesh.bindMatrixInverse).multiply(sk.bones[hi].matrixWorld.clone().multiply(sk.boneInverses[hi])).multiply(mesh.bindMatrix);
    const Ai = A.clone().invert(), g = mesh.geometry, pos = floats(g.attributes.position), si = g.attributes.skinIndex, sw = g.attributes.skinWeight;
    const nrm = g.attributes.normal ? floats(g.attributes.normal) : null, eps = (1e-4 * H) / A.getMaxScaleOnAxis();
    const pull = (x: THREE.Vector3) => {
      x.applyMatrix4(A);
      const dx = x.x - axis.x, dz = x.z - axis.z, r = Math.hypot(dx, dz), s = r <= R0 ? 1 : (R0 + (r - R0) * c) / r;
      return x.set(axis.x + dx * s, x.y, axis.z + dz * s).applyMatrix4(Ai);
    };
    for (let i = 0; i < pos.count; i++) {
      let w = 0;
      for (let j = 0; j < 4; j++) if (under[si.getComponent(i, j)]) w += sw.getComponent(i, j);
      v.fromBufferAttribute(pos, i).applyMatrix4(A);
      const r = Math.hypot(v.x - axis.x, v.z - axis.z);
      if (w < 0.5 || r <= R0 || !inHat(hat, r / H, (v.y - minY) / H)) continue;
      v.fromBufferAttribute(pos, i);
      if (nrm) { turnNormal(pull, v, n.fromBufferAttribute(nrm, i), eps); nrm.setXYZ(i, n.x, n.y, n.z); }
      pull(v);
      pos.setXYZ(i, v.x, v.y, v.z);
    }
    g.setAttribute('position', pos);
    if (nrm) g.setAttribute('normal', nrm);
    g.computeBoundingBox(); g.computeBoundingSphere();
  });
}

const J = new THREE.Matrix3(), col = [new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3()], back = new THREE.Vector3();
/** Turns normal n at v (both in the mesh's own space) by the inverse transpose of move's local stretch there, so it
 *  stays square to the moved surface. move works in place and returns its argument. */
function turnNormal(move: (x: THREE.Vector3) => THREE.Vector3, v: THREE.Vector3, n: THREE.Vector3, eps: number) {
  for (let j = 0; j < 3; j++) { // the move's stretch, column j: a central difference along local axis j
    col[j].copy(v).setComponent(j, v.getComponent(j) + eps); back.copy(v).setComponent(j, v.getComponent(j) - eps);
    move(col[j]).sub(move(back)).divideScalar(2 * eps);
  }
  J.set(col[0].x, col[1].x, col[2].x, col[0].y, col[1].y, col[2].y, col[0].z, col[1].z, col[2].z).invert().transpose();
  return n.applyMatrix3(J).normalize();
}

/** Puts spoked wheels on a chariot (Wheels), each a node named 'wheel' turning about the model's x (its axle). */
export function chariotWheels(scene: THREE.Object3D, w: Wheels | undefined) {
  if (!w) return [];
  scene.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(scene), H = box.max.y - box.min.y, c = box.getCenter(new THREE.Vector3());
  return w.at.map(([x, y, z]) => {
    const axle = new THREE.Group(), wheel = spokedWheel(w.r * H);
    wheel.name = 'wheel'; axle.rotation.y = Math.PI / 2; axle.add(wheel); // the wheel's axle (its z) across the car
    axle.position.set(c.x + x * H, box.min.y + y * H, c.z + z * H); axle.updateMatrixWorld(true);
    scene.attach(axle);
    return wheel;
  });
}

export function bakeUnit(name: string, g: Pick<GLTF, 'scene' | 'animations'>, army: ArmyId = 'samurai') {
  const clips = [...g.animations];
  // the body is measured before its weapon goes on: a yari in the box shifted the spearman 1.1 m off his spot (Astra)
  const holder = hold(g.scene);
  const body = proportion(name, g.scene, clips, ARMY_ART[army]?.face ?? FACE);
  applyProps(name, g.scene, clips, ARMY_PROPS[army]?.[name] ?? []); // a spear's pole, its carried walk and its made thrust
  const moves: Record<string, number> = {}, clipOf: Record<string, THREE.AnimationClip> = {};
  for (const [move, names] of Object.entries(CLIP_FOR)) {
    const clip = names.map((n) => clips.find((c) => c.name === n)).find(Boolean);
    if (clip) { moves[move] = clip.duration; clipOf[move] = clip; }
  }
  if (!clips.length) throw new Error(`${name}: no clips (every soldier is rigged; riders ride their horses, mount.ts)`);
  const mixer = new THREE.AnimationMixer(g.scene);
  const baked = bake(holder, moves, (move, t) => {
    const clip = clipOf[move];
    mixer.stopAllAction();
    mixer.clipAction(clip).play();
    aimProps(g.scene, clip.name);
    mixer.setTime(t);
  });
  baked.height = body; // the body sizes the class, not the yari, the banner or the hat
  return baked;
}

/** The crew's clips for an engine: a crew file with no idle (a spearman) stands in a stance from his walk; the ram's
 *  carriers move on the charge (its grip holds the log) and stand in it too. */
export function crewClips(crew: THREE.Object3D, clips: THREE.AnimationClip[], kind: SiegeKind) {
  const find = (n: string) => clips.find((c) => c.name === n);
  if (kind === 'ram' || kind === 'shed') { // the shed's crew push it on the charge's grip
    const run = find('run') ?? find('walk');
    if (!run) throw new Error('the ram crew have no run or walk clip');
    const walk = run.clone(); walk.name = 'walk';
    return [walk, standClip(crew, run, 'idle')];
  }
  if (find('idle') || !find('walk')) return clips;
  return [...clips, standClip(crew, find('walk')!, 'idle')];
}

export function bakeSiege(g: Pick<GLTF, 'scene'> | null, crew: Pick<GLTF, 'scene' | 'animations'>, art: ArmyArt = ARMY_ART.samurai!) {
  if (!art.siege || !art.crew) throw new Error('this set fields no engine');
  const root = CARTLESS.includes(art.siege) ? siegeRoot(art.siege) : g!.scene, rig = new SiegeRig(root, art.siege);
  const crewBody = proportion(art.crew, crew.scene, crew.animations, art.face), crewH = new THREE.Box3().setFromObject(crew.scene).getSize(new THREE.Vector3()).y;
  const clips = crewClips(crew.scene, crew.animations, art.siege);
  rig.mountCrew(crew.scene, clips);
  const holder = hold(root);
  // the walk is one crew stride, as a soldier's walk is one cycle of his clip, and the wheels turn once in it, so both
  // loop clean. At three strides the ram's carriers and every cart's crew bobbed 6.5 to 10 times a second, twice a
  // footman's steps, and read as bouncing (2026-10-26, out/bob.ts and out/cartbob.ts measures)
  const walk = rig.crewStride, speed = (2 * Math.PI * rig.radius) / walk;
  const idle = clips.find((c) => c.name === 'idle')?.duration ?? 1;
  const baked = bake(holder, { walk, attack: 2.0, idle }, (move, t) => rig.pose(move, t, speed));
  baked.height = (SiegeRig.CREW_H * crewBody) / crewH; // the crew's body in the engine's units: mountCrew stands them CREW_H tall, hats on
  return baked;
}

/** A mounted soldier put together at rest: the rider at his class's size with his weapon, the horse sized under him,
 *  the rider seated on its saddle bone with his legs astride. `clear` is each leg's clearance outside the barrel. */
export function mountRig(name: string, rider: Pick<GLTF, 'scene' | 'animations'>, horse: Pick<GLTF, 'scene' | 'animations'>, m: Mount, army: ArmyId = 'samurai') {
  const art = ARMY_ART[army];
  // the rider: standing, feet at 0, head to the army's share, his weapon on (measured first: the lance is not his body)
  const rs = rider.scene, rbox = new THREE.Box3().setFromObject(rs);
  rs.position.set(-(rbox.min.x + rbox.max.x) / 2, -rbox.min.y, -(rbox.min.z + rbox.max.z) / 2);
  const riderHolder = new THREE.Group(); riderHolder.add(rs);
  const raw = [...rider.animations], body = proportion(name, rs, raw, art?.face ?? FACE);
  const clips = riderClips(rs, raw);
  applyProps(name, rs, clips, ARMY_PROPS[army]?.[name] ?? []);
  // the horse: size riders tall, hooves at 0, centered
  const hs = horse.scene, hbox = new THREE.Box3().setFromObject(hs), k = ((m.size ?? HORSE_H) * body) / (hbox.max.y - hbox.min.y);
  hs.scale.multiplyScalar(k);
  hs.position.set(-k * (hbox.min.x + hbox.max.x) / 2, -k * hbox.min.y, -k * (hbox.min.z + hbox.max.z) / 2);
  const root = new THREE.Group(); root.add(hs); root.add(riderHolder);
  root.updateMatrixWorld(true);
  const saddle = saddleOf(hs);
  const clear = seatRider(riderHolder, hs, saddle);
  return { root, hs, rs, body, clips, saddle, clear };
}

/** A mounted class (2026-10-27, horses round; mount.ts): the rider (this class's file, rigged) seated on its horse,
 *  both baked as one unit. Moves: the horse's walk, trot and run (gaitRow picks one by ground speed, each with its
 *  stride), idle, the rider's attack over the horse's (a head butt, or a stand under an archer's shot, slowed to the
 *  rider's length), and the horse's death. The rider holds his ride stance in every move but the attack. */
export function bakeMounted(name: string, rider: Pick<GLTF, 'scene' | 'animations'>, horse: Pick<GLTF, 'scene' | 'animations'>, m: Mount, army: ArmyId = 'samurai') {
  const { root, hs, rs, body, clips } = mountRig(name, rider, horse, m, army);
  const hclip = (n: string) => {
    const c = horse.animations.find((x) => x.name === n);
    if (!c) throw new Error(`${name}'s horse has no ${n} clip`);
    return c;
  };
  const rclip = (n: string) => clips.find((c) => c.name === n);
  const attack = rclip('attack'), under = hclip(m.attack === 'stand' ? 'idle' : 'butt');
  const moves: Record<string, number> = {}, gait = ['walk', 'trot', 'run'];
  for (const g of [...gait, 'idle', 'death']) moves[g] = hclip(g).duration;
  moves.attack = attack?.duration ?? under.duration;
  const hm = new THREE.AnimationMixer(hs), rm = new THREE.AnimationMixer(rs);
  const baked = bake(root, moves, (move, t) => {
    hm.stopAllAction(); rm.stopAllAction();
    const hc = move === 'attack' ? under : hclip(move), rc = move === 'attack' && attack ? attack : rclip('ride')!;
    hm.clipAction(hc).play(); hm.setTime(move === 'attack' ? (t / moves.attack) * hc.duration : move === 'death' ? Math.min(t, hc.duration - 1e-3) : t);
    rm.clipAction(rc).play(); aimProps(rs, rc.name); rm.setTime(Math.min(t, rc.duration - 1e-3));
  });
  for (const g of gait) baked.moves[g].stride = strideOf(root, hclip(g));
  hm.stopAllAction(); rm.stopAllAction();
  baked.height = body; // the class is sized by its rider standing, as every class by its body
  return baked;
}

/** Chariot shares of the crew's standing height (2026-10-27, horses round): the car's rim at his waist (the old fused
 *  car's at 0.69 hid him to the chest, first render) and the wheels' radius a quarter; the axle is at the car's back
 *  (an Egyptian car's is), under its floor. */
const CAR_H = 0.5, WHEEL_R = 0.25, AXLE_Z = 0.18;
const pole = new THREE.MeshStandardMaterial({ color: '#6b4a2b', roughness: 0.6 });

/** The Egyptian chariot (2026-10-27, horses round): the car (this class's file, cut from the old fused model by
 *  scripts/cut_car.ts) on two code wheels, pulled by two rigged horses on a pole and yoke, with a driver and an archer
 *  (the crew files, the army's line soldier and bowman) standing in it. Moves as a mounted class's: the horses' gaits
 *  (each with its stride, the wheels rolling the ground it covers), idle, the archer's shot over the horses standing,
 *  and the horses' death with the car pitching onto its pole. `horses` and `crew` are parses of their own. */
export function bakeChariot(name: string, car: Pick<GLTF, 'scene'>, horses: Pick<GLTF, 'scene' | 'animations'>[], crew: Pick<GLTF, 'scene' | 'animations'>[], m: Mount, army: ArmyId = 'egyptians') {
  const art = ARMY_ART[army], files = m.crew ?? [];
  if (horses.length !== 2 || crew.length !== 2 || files.length !== 2) throw new Error(`${name}: a chariot takes two horses and a crew of two`);
  // the crew, standing (legs at rest, no hips slide), each to the driver's body
  const men = crew.map((c, k) => {
    const box = new THREE.Box3().setFromObject(c.scene);
    c.scene.position.set(-(box.min.x + box.max.x) / 2, -box.min.y, -(box.min.z + box.max.z) / 2);
    const holder = new THREE.Group(); holder.add(c.scene);
    const raw = [...c.animations], body = proportion(files[k], c.scene, raw, art?.face ?? FACE);
    const clips = riderClips(c.scene, raw);
    applyProps(files[k], c.scene, clips, ARMY_PROPS[army]?.[files[k]] ?? []);
    return { holder, scene: c.scene, body, clips };
  });
  const body = men[0].body;
  men[1].holder.scale.setScalar(body / men[1].body);
  const root = new THREE.Group(), carRoot = new THREE.Group(); // the car pitches about its axle
  root.add(carRoot);
  // the car: CAR_H bodies tall, its floor over the axle
  const cs = car.scene, cb = new THREE.Box3().setFromObject(cs), k = (CAR_H * body) / (cb.max.y - cb.min.y), r = WHEEL_R * body;
  cs.scale.multiplyScalar(k);
  const L = k * (cb.max.z - cb.min.z), W = k * (cb.max.x - cb.min.x);
  carRoot.position.set(0, r, -L / 2 + AXLE_Z * L); // the axle: the pitch's pivot
  cs.position.set(-k * (cb.min.x + cb.max.x) / 2, -k * cb.min.y - 0.25 * r, -k * (cb.min.z + cb.max.z) / 2 - carRoot.position.z);
  carRoot.add(cs); root.updateMatrixWorld(true);
  const H = CAR_H * body; // the car's height, the wheels' spec unit
  const wheels = chariotWheels(cs, { at: [-1, 1].map((s) => [s * (W / 2 + 0.05 * r) / H, 0.25 * r / H, -L / 2 / H + AXLE_Z * L / H] as [number, number, number]), r: WHEEL_R / CAR_H });
  // the crew, side by side on the floor: the driver on the left, the archer on the right
  men.forEach((man, j) => { man.holder.position.set((j ? 1 : -1) * 0.22 * W, 0.75 * r - r, L / 2 - carRoot.position.z - 0.5 * L); carRoot.add(man.holder); });
  // the horses, side by side ahead of the car, a touch apart
  const placed = horses.map((h, j) => {
    const hb = new THREE.Box3().setFromObject(h.scene), hk = ((m.size ?? HORSE_H) * body) / (hb.max.y - hb.min.y), hw = hk * (hb.max.x - hb.min.x);
    h.scene.scale.multiplyScalar(hk);
    h.scene.position.set((j ? 1 : -1) * (hw / 2 + 0.03 * body) - hk * (hb.min.x + hb.max.x) / 2, -hk * hb.min.y, L / 2 + 0.1 * body - hk * hb.min.z);
    root.add(h.scene);
    return { scene: h.scene, len: hk * (hb.max.z - hb.min.z), tall: hk * (hb.max.y - hb.min.y), x: (j ? 1 : -1) * (hw / 2 + 0.03 * body), z0: L / 2 + 0.1 * body };
  });
  // the pole, from the car's front under its floor to the horses' chests, and the yoke across their withers
  const h0 = placed[0], chest = new THREE.Vector3(0, 0.55 * h0.tall, h0.z0 + 0.62 * h0.len), from = new THREE.Vector3(0, 0.75 * r, L / 2);
  const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.035 * r, 0.035 * r, from.distanceTo(chest), 6), pole);
  shaft.position.copy(from).add(chest).multiplyScalar(0.5); shaft.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), chest.clone().sub(from).normalize());
  root.add(shaft);
  const yoke = new THREE.Mesh(new THREE.CylinderGeometry(0.04 * r, 0.04 * r, 2 * Math.abs(h0.x) + 0.3 * r, 6).rotateZ(Math.PI / 2), pole);
  yoke.position.set(0, 0.7 * h0.tall, h0.z0 + 0.6 * h0.len);
  root.add(yoke);
  // the whole team centered on the unit's spot, as every model is (hold): the car alone was a horse's length behind it
  const rig = new THREE.Group(), span = new THREE.Box3().setFromObject(root);
  root.position.z = -(span.min.z + span.max.z) / 2; rig.add(root);
  rig.updateMatrixWorld(true);
  // moves
  const clipOf = (n: string) => {
    const c = horses[0].animations.find((x) => x.name === n);
    if (!c) throw new Error(`${name}'s horses have no ${n} clip`);
    return c;
  };
  const gait = ['walk', 'trot', 'run'], moves: Record<string, number> = {}, strides: Record<string, number> = {};
  for (const g of [...gait, 'idle', 'death']) moves[g] = clipOf(g).duration;
  const shot = men[1].clips.find((c) => c.name === 'attack');
  moves.attack = shot?.duration ?? moves.idle;
  for (const g of gait) strides[g] = strideOf(root, clipOf(g));
  const hm = placed.map((p) => new THREE.AnimationMixer(p.scene)), cm = men.map((man) => new THREE.AnimationMixer(man.scene));
  const baked = bake(rig, moves, (move, t) => {
    const hc = clipOf(move === 'attack' ? 'idle' : move), u = t / moves[move];
    // the second horse a little behind the first in his stride, so the pair do not move as one
    hm.forEach((mx, j) => { mx.stopAllAction(); mx.clipAction(hc).play(); mx.setTime(move === 'death' ? Math.min(t, hc.duration - 1e-3) : (move === 'attack' ? u * hc.duration : t) + j * 0.12 * hc.duration); });
    men.forEach((man, j) => {
      const c = j === 1 && move === 'attack' && shot ? shot : man.clips.find((x) => x.name === 'ride')!;
      cm[j].stopAllAction(); cm[j].clipAction(c).play(); aimProps(man.scene, c.name); cm[j].setTime(Math.min(t, c.duration - 1e-3));
    });
    // the wheels roll the ground a cycle covers (a whole number of spokes' turns, so the loop has no seam: six spokes)
    const turn = gait.includes(move) ? Math.round(strides[move] / r / (Math.PI / 6)) * (Math.PI / 6) : 0;
    for (const w of wheels) w.rotation.z = u * turn;
    carRoot.rotation.x = move === 'death' ? Math.min(1, t / moves.death) * 0.18 : 0;
  });
  for (const g of gait) baked.moves[g].stride = strides[g];
  baked.height = body; // the class is sized by its crew standing
  return baked;
}
